// NPM
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import moment, { Moment } from "moment";
import { useTranslation } from "react-i18next";
import { isNil, isEmpty, isUndefined, omitBy } from "lodash";

// ANT COMPONENT
import { ArrowLeftOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import {
  AlertBox,
  Button,
  PhoneInput,
  SelectInput,
  TextInput,
  TimePicker,
} from "@life-line-lab/components-react";
import { Checkbox, Col, Row, Spin } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";

// STYLE
import "./customer-location-form.scss";
import "./../customer-management.scss";

// IMPORT TYPE
import { CustomerFormInputType, PhoneType } from "../../../types/customer-form.types";

// VALIDATOR
import { Day, ShallowClonedTypeFor } from "../../../types/common.types";

// IMPORT DATA
import countryList from "../../../data/country.json";
import stateList from "../../../data/state.json";

// UTILS
import errorHandler from "Utils/error-handler";
import CustomerLocationFormSchema from "./customer-location-form.validation";

// TYPES
import { CustomerLocationFormType } from "Types/customer_location.types";

// HOOKS
import useSession from "Hooks/session.hook";
import useStatesList from "Hooks/states-option.hook";
import useCountriesList from "Hooks/countries-option.hook";
import { toaster } from "Helpers/toaster";
import { validateGeocoding } from "Services/geocoding.services";

interface CustomerLocationFormComponentProps {
  onBack: () => void;
  onSubmit: (data) => void;
  onSubmitEdit: (data) => void;
  customerList: any[];
  locationDetails: Partial<CustomerLocationFormType>;
  canViewCustomerList: boolean;
  isLoading?: boolean;
}

interface CustomerFormErrorType
  extends ShallowClonedTypeFor<Partial<CustomerLocationFormType>, string | null> {
  general?: string;
}

// =========================================================
//  Tracks working hours
// =========================================================
const initialWorkingHours = [
  {
    day: "MONDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
  {
    day: "TUESDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
  {
    day: "WEDNESDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
  {
    day: "THURSDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
  {
    day: "FRIDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
  {
    day: "SATURDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
  {
    day: "SUNDAY",
    closingTime: null,
    openingTime: null,
    isChecked: false,
  },
];

const useDaysChecked = (input, setInput, error, setError) => {
  const modifyState = (currentInput) => {
    setInput((prevInput) => ({ ...prevInput, workingHours: currentInput }));
  };

  const setDays = (e, day) => {
    setError({ ...error, workingHours: null });
    const dayIsChecked = e.target.checked;

    //  if the everyday checkbox is checked, set all isCheck to true
    // ================================================================
    if (day == Day.everyday) {
      if (dayIsChecked) {
        let workingHours = input?.workingHours?.map((current) => {
          return {
            ...current,
            isChecked: true,
          };
        });

        modifyState(workingHours);
      } else {
        //  if the everyday checkbox is unchecked, set all isCheck to false and clear time
        // =================================================================================
        let workingHours = input?.workingHours?.map((current) => {
          return {
            ...current,
            openingTime: null,
            closingTime: null,
            isChecked: false,
          };
        });

        modifyState(workingHours);
      }
    } else {
      //  if a day is checked, set that day's isCheck to true
      // =========================================================================
      if (dayIsChecked) {
        let workingHours = input?.workingHours?.map((current) => {
          if (day == current?.day) {
            return {
              ...current,
              isChecked: true,
            };
          } else {
            return current;
          }
        });

        modifyState(workingHours);
      } else if (!dayIsChecked) {
        //  if a day is unchecked, set that day's isCheck to false and clear time
        // =========================================================================
        let workingHours = input?.workingHours?.map((current) => {
          if (day == current?.day) {
            return {
              ...current,
              openingTime: null,
              closingTime: null,
              isChecked: false,
            };
          } else {
            return current;
          }
        });

        modifyState(workingHours);
      }
    }
  };

  return {
    setDays,
  };
};

// =========================================================
//  Form Handlers
// =========================================================
const useFormHandlers = (props) => {
  const {
    input,
    error,
    setDays,
    setIndeterminate,
    setIsAllSelected,
    setInput,
    setError,
    customerList,
  } = props || {};

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput({ ...input, [e.target.name]: e.target.value });
    setError({ ...error, [e.target.name]: null });
  };

  const handleSelectChange = (value: string | string[], name: string) => {
    setError({ ...error, [name]: null });

    switch (name) {
      case "customerID": {
        const orgType = customerList.find((customer) => customer._id === value).orgType;
        const customerName = customerList.find((customer) => customer._id === value).name;
        setInput({ ...input, [name]: value as string, orgType, customerName });
        break;
      }
      case "country": {
        setInput({ ...input, [name]: value as string, state: undefined });
        break;
      }
      default: {
        setInput({ ...input, [name]: value });
        break;
      }
    }
  };

  const handlePhoneChange = (value: { dialCode: string; number: string }, name: string) => {
    setError({ ...error, [name]: null });
    setInput({ ...input, [name]: value });
  };

  const handleTimeChange = (time: Moment | null, day: Day, type: "openingTime" | "closingTime") => {
    // clear error
    setError({ ...error, workingHours: null });

    // sets the time in which the day is checked
    // if openingTime is passed, set openingTime, otherwise set closingTime
    // when one is set, the other takes either existing value or null
    setInput((prevInput) => ({
      ...prevInput,
      workingHours: prevInput?.workingHours?.map((current) => {
        if (current?.day == day) {
          return {
            ...current,
            openingTime: type == "openingTime" ? time : current?.openingTime ?? null,
            closingTime: type == "closingTime" ? time : current?.closingTime ?? null,
          };
        } else {
          return current;
        }
      }),
    }));
  };

  const handleWorkingHourChange = (e: CheckboxChangeEvent, day: Day) => {
    let numberOfChecks = 0;

    // count the number of checked boxes
    // 7 because there are 7 possible working days (Monday -> Sunday)
    for (let i = 0; i < 7; i++) {
      if (input?.workingHours && input?.workingHours[i]?.isChecked) {
        numberOfChecks++;
      }
    }

    // if the day is checked, but not select all, add into numberOfChecks
    if (day !== Day.everyday && e.target.checked) {
      numberOfChecks++;
    }

    // if selectAll is checked by user OR numberOfChecks = 7, check the selectAll box
    if (day === Day.everyday || numberOfChecks == 7) {
      setIndeterminate && setIndeterminate(false);

      if (e.target.checked) setIsAllSelected && setIsAllSelected(true);
      else setIsAllSelected && setIsAllSelected(false);
    } else {
      // assume at least one is checked (cant be 7 because that is handled in the above if)
      setIndeterminate && setIndeterminate(true);
      // unset indeterminate if numberOfChecks is 0
      if (numberOfChecks === 0) setIndeterminate && setIndeterminate(false);
    }

    setDays && setDays(e, day);
  };

  const handleValidateAddress = async (address: any) => {
    const responseData = await validateGeocoding(address);
    if (responseData && !responseData.error) {
      if (responseData?.verdict) return true;
      else return false;
    } else {
      return false;
    }
  };

  return {
    handleFieldChange,
    handlePhoneChange,
    handleTimeChange,
    handleSelectChange,
    handleWorkingHourChange,
    handleValidateAddress,
  };
};

// =========================================================
//  Operational Hours Component
// =========================================================
const OperationalHoursComponent = (props) => {
  const { indeterminate, isAllSelected, input, error, handleWorkingHourChange, handleTimeChange } =
    props || {};
  const { t } = useTranslation();

  return (
    <div className="oh-container">
      <div>
        <label className="inp-label inp-label--required">{t("label.operationalHours")}</label>
      </div>
      <div className="oh-container-inner">
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <div className="oh-select-all">
              <Checkbox
                indeterminate={indeterminate}
                checked={isAllSelected}
                onChange={(e) => handleWorkingHourChange(e, "everyday" as Day)}
              >
                {t(`label.selectAll`)}
              </Checkbox>
              {isAllSelected && (
                <div className="cm-info cm-help">
                  <ExclamationCircleOutlined style={{ fontSize: "16px" }} />
                  <div>{t("txt.setOperationalHoursEveryDay")}</div>
                </div>
              )}
            </div>
          </Col>
          <div className="oh-divider"></div>
          <Col span={24}>
            <Row gutter={[32, 0]}>
              <AlertBox text={error.workingHours} />
              <Col span={12}>
                <Row gutter={[16, 16]} align="middle">
                  {input?.workingHours?.map((current, index) => {
                    if (index < 4)
                      return (
                        <>
                          <Col span={8}>
                            <Checkbox
                              checked={current?.isChecked}
                              onChange={(e) => handleWorkingHourChange(e, current?.day as Day)}
                            >
                              {t(`label.${current?.day?.toUpperCase()}`)}
                            </Checkbox>
                          </Col>
                          <Col span={8}>
                            <TimePicker
                              format={"hh:mm a"}
                              placeholder={t("txt.openingHours")}
                              value={current?.openingTime}
                              onChange={(time) => {
                                handleTimeChange(time, current?.day as Day, "openingTime");
                              }}
                            />
                          </Col>
                          <Col span={8}>
                            <TimePicker
                              format={"hh:mm a"}
                              placeholder={t("txt.closingHours")}
                              value={current?.closingTime}
                              onChange={(time) => {
                                handleTimeChange(time, current?.day as Day, "closingTime");
                              }}
                            />
                          </Col>
                        </>
                      );
                  })}
                </Row>
              </Col>
              <Col span={12}>
                <Row gutter={[16, 16]} align="middle">
                  {input?.workingHours?.map((current, index) => {
                    if (index >= 4)
                      return (
                        <>
                          <Col span={8}>
                            <Checkbox
                              checked={current?.isChecked}
                              onChange={(e) => handleWorkingHourChange(e, current?.day as Day)}
                            >
                              {t(`label.${current?.day?.toUpperCase()}`)}
                            </Checkbox>
                          </Col>
                          <Col span={8}>
                            <TimePicker
                              format={"hh:mm a"}
                              placeholder={t("txt.openingHours")}
                              value={current?.openingTime}
                              onChange={(time) => {
                                handleTimeChange(time, current?.day as Day, "openingTime");
                              }}
                            />
                          </Col>
                          <Col span={8}>
                            <TimePicker
                              format={"hh:mm a"}
                              placeholder={t("txt.closingHours")}
                              value={current?.closingTime}
                              onChange={(time) => {
                                handleTimeChange(time, current?.day as Day, "closingTime");
                              }}
                            />
                          </Col>
                        </>
                      );
                  })}
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </div>
  );
};

// =========================================================
//  Main Component
// =========================================================
const CustomerLocationFormComponent: React.FC<CustomerLocationFormComponentProps> = ({
  onBack,
  onSubmit,
  onSubmitEdit,
  customerList,
  locationDetails,
  canViewCustomerList,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { org } = useSession();

  //  Initial Input
  // ===================================
  const initialInput: Partial<CustomerLocationFormType> = {
    workingHours: [...initialWorkingHours],
    primary: false,
  };
  const initialError = {};

  //  All states
  // ===================================
  const [input, setInput] = useState<Partial<CustomerLocationFormType>>(initialInput);
  const [error, setError] = useState<CustomerFormErrorType>(initialError);
  const [indeterminate, setIndeterminate] = useState<boolean>(false);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);

  //  Hooks
  // ===================================
  const stateOption = useStatesList(input?.country);
  const countryOption = useCountriesList();

  const { setDays } = useDaysChecked(input, setInput, error, setError);

  const formHandlerProps = {
    input,
    error,
    setDays,
    setIndeterminate,
    setIsAllSelected,
    setInput,
    setError,
    customerList,
  };

  const {
    handleFieldChange,
    handleWorkingHourChange,
    handlePhoneChange,
    handleTimeChange,
    handleSelectChange,
    handleValidateAddress,
  } = useFormHandlers({ ...formHandlerProps });

  //  Populate Location Details
  // ===================================
  useEffect(() => {
    if (locationDetails) {
      // map response to workingHours
      locationDetails?.workingHours?.map((workingHour) => {
        let tempHours = {
          day: workingHour?.day,
          openingTime: moment(workingHour?.openingTime, "YYYY-MM-DD hh:mm a Z"),
          closingTime: moment(workingHour?.closingTime, "YYYY-MM-DD hh:mm a Z"),
          isChecked: true,
        };

        // replace appropriately
        setInput((prevInput) => ({
          ...prevInput,
          workingHours: prevInput?.workingHours?.map((hour) => {
            if (hour?.day == workingHour?.day) {
              return tempHours;
            } else {
              return hour;
            }
          }),
        }));
      });

      // check if it is undefined
      if (locationDetails?.workingHours) {
        // if company has all working hours, check the select all box
        if (locationDetails?.workingHours?.length == 7) setIsAllSelected && setIsAllSelected(true);
        // if company has at least one, set indeterminate
        else if (locationDetails?.workingHours?.length > 0)
          setIndeterminate && setIndeterminate(true);
      }

      const { workingHours, ...otherDetails } = locationDetails;

      setInput((prevInput) => ({
        ...prevInput,
        ...otherDetails,
        customerLocationName: locationDetails?.customerLocationName ?? null,
      }));
    }

    if (!canViewCustomerList) {
      setInput((prevInput) => ({
        ...prevInput,
        customerID: org._id,
        orgType: org.orgType,
      }));
      customerList.push(org);
    }
  }, [locationDetails]);

  const miniAddressNormaliser = (data, omitStreet = false) => {
    return omitBy(
      {
        street1: omitStreet ? undefined : data.street1,
        street2: omitStreet ? undefined : data.street2,
        city: data.city,
        state: data.state,
        postcode: data.postcode,
        country: data.country,
      },
      isUndefined,
    );
  };

  //  Submit
  // ===================================
  const handleSubmit = async () => {
    try {
      const value = CustomerLocationFormSchema.validateSync(
        omitBy(
          {
            ...input,
          },
          isUndefined,
        ),
        {
          strict: true,
          abortEarly: true,
          stripUnknown: false,
          context: { data: { ...input, indeterminate } },
        },
      );

      if (!isEmpty(value)) {
        setIsLoadingSubmit && setIsLoadingSubmit(true);
        const addressToCheck = miniAddressNormaliser(input, true);
        const addressValidity = await handleValidateAddress(addressToCheck);

        if (addressValidity) {
          if (input._id) {
            if (onSubmitEdit) {
              onSubmitEdit({ ...value });
            }
          } else {
            if (onSubmit) {
              onSubmit({ ...value });
            }
          }
        } else {
          toaster("error", t("error.addressNotFound"));
        }
      } else {
        setError({ ...error, general: t("error.validationFailed") });
      }

      setIsLoadingSubmit && setIsLoadingSubmit(false);
    } catch (err) {
      let { path, value, message }: any = errorHandler.validation(err);
      console.log(path, value, err);

      // if path is an array
      let isPathArray = false;

      // error for workingHours
      if (path.indexOf("[") !== -1) {
        isPathArray = true;
        let checkValue = true;
        const errorPath = path.split("[");

        switch (message) {
          case "beforeOpeningTime":
            checkValue = false;
            setError({
              ...error,
              [errorPath[0]]: t(`error.beforeOpeningTime`, { field: t(`label.${path}`) }),
            });
            break;
          case "workingHoursRequired":
            checkValue = false;
            setError({
              ...error,
              [errorPath[0]]: t(`error.workingHoursRequired`),
            });
            break;
          default:
        }

        if (checkValue) {
          switch (value) {
            case "error.pattern":
              setError({
                ...error,
                [errorPath[0]]: t(`error.pattern`, { field: t(`label.${path}`) }),
              });
              break;
            case "error.validationFailed":
              setError({
                ...error,
                [errorPath[0]]: t(`error.required`, { field: t(`label.${path}`) }),
              });
              break;
            default:
              setError({
                ...error,
                [errorPath[0]]: t(`${value}`, { field: t(`label.${path}`) }),
              });
          }
        }
      }

      // e.g. contactNumber.number = contactNumber
      if (path.indexOf(".") !== -1) {
        const str = path.split(".");
        path = str[0];
      }

      if (!isPathArray) {
        // validation message
        switch (value) {
          case "error.pattern":
            setError({
              ...error,
              [path]: t(`error.pattern`, { field: t(`label.${path}`) }),
            });
            break;
          case "error.validationFailed":
            if (message == "reservedName") {
              setError({
                ...error,
                [path]: t(`error.invalidLocationName`),
              });
            } else {
              setError({
                ...error,
                [path]: t(`error.required`, { field: t(`label.${path}`) }),
              });
            }
            break;
          default:
            setError({
              ...error,
              [path]: t(`${value}`, { field: t(`label.${path}`) }),
            });
        }

        if (document.querySelector(`input[name=${path}]`)) {
          (document.querySelector(`input[name=${path}]`) as HTMLInputElement).focus();
        } else if (document.querySelector(`div[name=${path}]`)) {
          (document.querySelector(`div[name=${path}] input`) as HTMLInputElement).focus();
        }
      }

      setIsLoadingSubmit && setIsLoadingSubmit(false);
      toaster("error", t("txt.resolveFormError"));
    }
  };

  return (
    <div className="customer-management customer-location-form">
      {isLoading || isLoadingSubmit ? (
        <div className="loading-container">
          <Spin size="large" />
        </div>
      ) : (
        <form>
          <div className="cm-form-section">
            <div className="cm-form-title">
              {input._id ? (
                <span
                  className="cursor-pointer back-action"
                  onClick={() => {
                    onBack ? onBack() : {};
                  }}
                >
                  <ArrowLeftOutlined />
                  <span className="ml-5">{t("txt.back")}</span>
                </span>
              ) : null}
              <span className="header-title">{t("title.customerLocation").toUpperCase()}</span>
            </div>
            <div className="cm-form-content">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={24} md={12}>
                  <SelectInput
                    showSearch
                    isRequired={true}
                    isReadonly={input._id || !canViewCustomerList ? true : false}
                    label={t("label.customerName").toUpperCase()}
                    placeholder={t("txt.customerNameExample")}
                    value={input.customerID}
                    name="customerID"
                    filterOption={(inputValue, option) =>
                      option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                    }
                    options={customerList.map((customer) => {
                      return {
                        value: customer._id,
                        label: customer.name,
                      };
                    })}
                    onChange={(value: string | string[]) => handleSelectChange(value, "customerID")}
                  />
                  <AlertBox text={error.customerID} />
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <TextInput
                    isRequired={true}
                    isReadonly={input.primary}
                    label={t("label.customerLocationName")}
                    placeholder={t("txt.customerLocation")}
                    value={input.customerLocationName}
                    name="customerLocationName"
                    onChange={handleFieldChange}
                  />
                  <AlertBox text={error.customerLocationName} />
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <label className="inp-label inp-label--required">
                    {t("label.registeredAddress").toUpperCase()}
                  </label>
                  <div className="mb-15">
                    <TextInput
                      isRequired={true}
                      isReadonly={input.primary}
                      placeholder={t("txt.street1Example")}
                      value={input.street1}
                      name="street1"
                      onChange={handleFieldChange}
                    />
                    <AlertBox text={error.street1} />
                  </div>
                  <div className="mb-15">
                    <TextInput
                      isRequired={true}
                      isReadonly={input.primary}
                      placeholder={t("txt.street2Example")}
                      value={input.street2}
                      name="street2"
                      onChange={handleFieldChange}
                    />
                    <AlertBox text={error.street2} />
                  </div>
                  <Row gutter={[15, 15]}>
                    <Col xs={24} sm={24} md={12} className="mb-0">
                      <TextInput
                        isRequired={true}
                        isReadonly={input.primary}
                        placeholder={t("txt.postcode")}
                        value={input.postcode}
                        name="postcode"
                        onChange={handleFieldChange}
                      />
                      <AlertBox text={error.postcode} />
                    </Col>
                    <Col xs={24} sm={24} md={12} className="mb-0">
                      <TextInput
                        isRequired={true}
                        isReadonly={input.primary}
                        placeholder={t("txt.city")}
                        value={input.city}
                        name="city"
                        onChange={handleFieldChange}
                      />
                      <AlertBox text={error.city} />
                    </Col>
                    <Col xs={24} sm={24} md={12} className="mb-0">
                      <SelectInput
                        showSearch
                        filterOption={(inputValue, option) =>
                          option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                        }
                        options={stateOption}
                        value={input.state}
                        isReadonly={isNil(input.country) || input.primary}
                        name="state"
                        placeholder={t("option.state")}
                        onChange={(value: string | string[]) => handleSelectChange(value, "state")}
                      />
                      <AlertBox text={error.state} />
                    </Col>
                    <Col xs={24} sm={24} md={12} className="mb-0">
                      <SelectInput
                        showSearch
                        filterOption={(inputValue, option) =>
                          option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                        }
                        options={countryOption}
                        value={input.country}
                        isReadonly={input.primary}
                        name="country"
                        placeholder={t("option.country")}
                        onChange={(value: string | string[]) =>
                          handleSelectChange(value, "country")
                        }
                      />
                      <AlertBox text={error.country} />
                    </Col>
                  </Row>
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <div className="mb-15">
                    <TextInput
                      isRequired={true}
                      isReadonly={input.primary}
                      label={t("label.contactPerson").toUpperCase()}
                      placeholder={t("txt.contactPerson")}
                      value={input.contactPerson}
                      name="contactPerson"
                      onChange={handleFieldChange}
                    />
                    <AlertBox text={error.contactPerson} />
                  </div>
                  <div className="mb-15">
                    <TextInput
                      isRequired={true}
                      isReadonly={input.primary}
                      label={t("label.contactEmailAddress")}
                      placeholder={t("txt.emailAddressExample")}
                      value={input.emailAddress}
                      name="emailAddress"
                      onChange={handleFieldChange}
                    />
                    <AlertBox text={error.emailAddress} />
                  </div>
                  <div>
                    <PhoneInput
                      isRequired={true}
                      isReadonly={input.primary}
                      isError={!!error.contactNo}
                      label={t("label.contactNo")}
                      name="contactNo"
                      value={input.contactNo}
                      onChange={(value) => handlePhoneChange(value, "contactNo")}
                    />
                    <AlertBox text={error.contactNo} />
                  </div>
                </Col>
                <Col xs={24} sm={24}>
                  <TextInput
                    label={t("label.handlingInstructions")}
                    placeholder={t("txt.enterHere")}
                    value={input.handlingInstructions}
                    name="handlingInstructions"
                    onChange={handleFieldChange}
                  />
                </Col>
                <Col xs={24} sm={24}>
                  <OperationalHoursComponent
                    indeterminate={indeterminate}
                    isAllSelected={isAllSelected}
                    input={input}
                    error={error}
                    handleWorkingHourChange={handleWorkingHourChange}
                    handleTimeChange={handleTimeChange}
                  />
                </Col>
                <AlertBox text={error.general} />
              </Row>
            </div>
            <div className="my-50 center">
              <Button
                label={input._id ? t("action.save") : t("action.submit")}
                type="primary"
                onClick={() => {
                  handleSubmit();
                }}
              />
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default CustomerLocationFormComponent;
